import { create } from 'zustand';
import type {
  AttributeDto,
  CreateAttributeRequest,
  ProductAttributeSlotDto,
  ProductAttributeValueInput,
  UpdateAttributeRequest,
} from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * Product specifications (API → Store → Render, ADR-008).
 *
 * Three surfaces share one store because they are one feature: the library, a
 * category's template, and one product's answers. Keeping them apart would mean
 * three stores that all have to agree about what an attribute is.
 */

interface AttributesState {
  library: AttributeDto[];
  loading: boolean;
  saving: boolean;
  error: string | null;

  /** What the category chosen on a new product asks, inheritance included. */
  categoryAttributes: AttributeDto[];
  /** Which category `categoryAttributes` belongs to — a late answer for another is dropped. */
  categoryAttributesFor: string | null;
  /** The slots of whichever product is open. */
  productSlots: ProductAttributeSlotDto[];
  /** Which product `productSlots` belongs to. */
  productSlotsFor: string | null;

  /**
   * The template editor's category (ADR-053): what it declares itself, in its
   * order, and what its parent categories already ask — shown, not edited here.
   */
  template: { categoryId: string; own: AttributeDto[]; fromParents: AttributeDto[] } | null;
  templateLoading: boolean;

  load: () => Promise<void>;
  /** The created attribute, so a screen that made it can use it straight away. */
  create: (body: CreateAttributeRequest) => Promise<AttributeDto | null>;
  update: (id: string, body: UpdateAttributeRequest) => Promise<boolean>;
  deactivate: (id: string) => Promise<void>;

  loadForCategory: (categoryId: string) => Promise<void>;
  loadTemplate: (categoryId: string, parentId: string | null) => Promise<void>;
  closeTemplate: () => void;
  /** Replaces the category's own template; the order given is the order asked. */
  setForCategory: (
    categoryId: string,
    entries: { attributeId: string; isRequired?: boolean }[],
  ) => Promise<boolean>;

  loadForProduct: (productId: string) => Promise<void>;
  setForProduct: (productId: string, values: ProductAttributeValueInput[]) => Promise<boolean>;
}

/** The category whose template was asked for last; an answer for any other is stale. */
let templateRequest: string | null = null;

export const useAttributesStore = create<AttributesState>((set, get) => ({
  library: [],
  loading: false,
  saving: false,
  error: null,
  categoryAttributes: [],
  categoryAttributesFor: null,
  productSlots: [],
  productSlotsFor: null,
  template: null,
  templateLoading: false,

  async load() {
    set({ loading: true, error: null });

    try {
      const page = await withSession(() => adminApi.attributes.list());
      set({ library: page.items, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the attribute library.') });
    }
  },

  async create(body) {
    set({ saving: true, error: null });

    try {
      const created = await withSession(() => adminApi.attributes.create(body));
      set({ saving: false, library: [...get().library, created] });
      return created;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not save this attribute.') });
      return null;
    }
  },

  async update(id, body) {
    set({ saving: true, error: null });

    try {
      const updated = await withSession(() => adminApi.attributes.update(id, body));
      set({ saving: false, library: get().library.map((item) => (item.id === id ? updated : item)) });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not save this attribute.') });
      return false;
    }
  },

  async deactivate(id) {
    set({ saving: true, error: null });

    try {
      const updated = await withSession(() => adminApi.attributes.deactivate(id));
      set({ saving: false, library: get().library.map((item) => (item.id === id ? updated : item)) });
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not retire this attribute.') });
    }
  },

  async loadForCategory(categoryId) {
    // Cleared first: showing the previous category's template under a new name
    // is how somebody assigns the wrong questions.
    set({ categoryAttributes: [], categoryAttributesFor: categoryId, error: null });

    try {
      const page = await withSession(() => adminApi.attributes.forCategory(categoryId));
      // Choosing another category while this one loaded: its answer is stale.
      if (get().categoryAttributesFor === categoryId) set({ categoryAttributes: page.items });
    } catch (error) {
      set({ error: errorMessage(error, 'Could not load this category template.') });
    }
  },

  async loadTemplate(categoryId, parentId) {
    templateRequest = categoryId;
    set({ template: null, templateLoading: true, error: null });

    try {
      // The parent's whole template is exactly what this category inherits:
      // subtracting its own rows from its full template cannot tell an
      // attribute it declares from one it shares with an ancestor.
      const [own, fromParents] = await Promise.all([
        withSession(() => adminApi.attributes.forCategory(categoryId, { own: true })),
        parentId ? withSession(() => adminApi.attributes.forCategory(parentId)) : Promise.resolve(null),
      ]);
      if (templateRequest !== categoryId) return;
      set({
        templateLoading: false,
        template: { categoryId, own: own.items, fromParents: fromParents?.items ?? [] },
      });
    } catch (error) {
      if (templateRequest !== categoryId) return;
      set({ templateLoading: false, error: errorMessage(error, 'Could not load this category template.') });
    }
  },

  closeTemplate() {
    templateRequest = null;
    set({ template: null, templateLoading: false, error: null });
  },

  async setForCategory(categoryId, entries) {
    set({ saving: true, error: null });

    try {
      const page = await withSession(() => adminApi.attributes.setForCategory(categoryId, entries));
      const template = get().template;
      set({
        saving: false,
        template: template?.categoryId === categoryId ? { ...template, own: page.items } : template,
      });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not save the template.') });
      return false;
    }
  },

  async loadForProduct(productId) {
    set({ productSlots: [], productSlotsFor: productId, error: null });

    try {
      const page = await withSession(() => adminApi.attributes.forProduct(productId));
      if (get().productSlotsFor === productId) set({ productSlots: page.items });
    } catch (error) {
      set({ error: errorMessage(error, 'Could not load the specifications.') });
    }
  },

  async setForProduct(productId, values) {
    set({ saving: true, error: null });

    try {
      const page = await withSession(() => adminApi.attributes.setForProduct(productId, values));
      set({ saving: false, productSlots: page.items });
      return true;
    } catch (error) {
      // The API names what went wrong — an option from another attribute, two
      // choices where one is allowed — and that message is the useful one.
      set({ saving: false, error: errorMessage(error, 'Could not save the specifications.') });
      return false;
    }
  },
}));
